import { useEffect } from 'react';
import useMarketInfo from './useMarketInfo';
import { isTrustlyCompleted } from '../components/trustlyIframe/trustlyUtils';
import { useRegistrationStore } from '../store/store';
import { FLOW_NAMES } from '../utils/countries';
import { gatekeeper } from '../utils/gatekeeper';

const PNP_STEPS_WITHOUT_CLOSE = new Set([
  'pnp_welcome_offer',
  'pnp_email_password_phone',
  'pnp_deposit_limits',
  'pnp_marketing_consent',
]);

/**
 * In the PnP flow the registration X must stay hidden once a deposit has
 * been initiated (Trustly iframe loaded/completed) and across the 4
 * post-deposit acquisition steps so players cannot bail out and trigger
 * a refund. The cashier (pre-deposit) keeps its X. Also fires a native
 * bridge event so the host app mirrors the web-side close state.
 */
const useShouldHidePnpClose = (): boolean => {
  const { flowName } = useMarketInfo();
  const trustlyCheckoutUrl = useRegistrationStore(
    (state) => state.metadata?.trustlyCheckoutUrl as string | undefined,
  );
  const currentStep = useRegistrationStore(
    (state) => state.metadata?.currentStep as string | undefined,
  );

  const isPnpFlow = flowName === FLOW_NAMES.MGA_GRA_PNP;
  const isOnPnpRegistrationStep = currentStep
    ? PNP_STEPS_WITHOUT_CLOSE.has(currentStep)
    : false;

  const shouldHide = isPnpFlow && (
    isOnPnpRegistrationStep
    || Boolean(trustlyCheckoutUrl)
    || isTrustlyCompleted()
  );

  useEffect(() => {
    if (!isPnpFlow) return;
    gatekeeper?.messageBus?.publish('native', {
      kind: 'hideNativeClose',
      hide: shouldHide,
    });
  }, [shouldHide, isPnpFlow]);

  return shouldHide;
};

export default useShouldHidePnpClose;
